/** New single-column card login shell (new-brand theme expected from parent ThemeProvider on portal New routes). */
import { FC, useEffect } from 'react';
import { Box, Paper, Stack, Typography } from '@mui/material';
import { getAppConfig } from '../../../../config';
import { OperationTookTooLongGuard } from '../../Molecules';
import { turnDedupForeverOff, turnDedupForeverOn } from '../../../../services/utils/axiosOptimizer';
import { Alert } from '../../../../ui';
import { LoginPageContent } from './LoginPageContent';
import { LoginCardFooter } from './LoginCardFooter';
import { ILoginContainerProps } from './types';

const LoginContainerNew: FC<ILoginContainerProps> = ({
    logo,
    formVariant,
    userLogin,
    isLoading,
    email,
    password,
    companyId,
    emailError,
    passwordError,
    companyIdError,
    errorMessage,
    successMessage,
    handleEmailChange,
    handlePasswordChange,
    handleCompanyIdChange,
    handleSignIn,
    handleForgotPassword,
    handleReset,
    handleBackToSignIn,
    handleSSOSignIn,
    handleLogInAgain,
    isBuyer = false,
}) => {
    useEffect(() => {
        if (isLoading) {
            turnDedupForeverOn();
        } else {
            turnDedupForeverOff();
        }

        return () => {
            turnDedupForeverOff();
        };
    }, [isLoading]);

    const ssoHeader =
        formVariant === 'sso' ? (
            <Box
                sx={{
                    width: '100%',
                    textAlign: 'center',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                }}
            >
                <Box sx={{ mb: 1, '& img': { maxWidth: '100%', maxHeight: 120, objectFit: 'contain' } }}>
                    <img
                        alt={userLogin.accountProfile?.SSOAttributes?.root?.customerLogo?.alternateText}
                        src={userLogin.accountProfile?.SSOAttributes?.root?.customerLogo?.url}
                    />
                </Box>
                {userLogin.accountProfile?.SSOAttributes?.root?.customWelcomeText ? (
                    <Typography component="span" variant="body2" color="text.secondary">
                        {userLogin.accountProfile?.SSOAttributes?.root?.customWelcomeText}
                    </Typography>
                ) : (
                    userLogin.accountProfile?.SSOAttributes?.root?.customText && (
                        <Box
                            sx={{ color: 'text.secondary', typography: 'body2' }}
                            // eslint-disable-next-line react/no-danger
                            dangerouslySetInnerHTML={{
                                __html: userLogin.accountProfile.SSOAttributes.root.customText,
                            }}
                        />
                    )
                )}
            </Box>
        ) : null;

    return (
        <Box
            sx={{
                width: '100%',
                minHeight: '100%',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                py: 3,
                px: 2,
                boxSizing: 'border-box',
            }}
        >
            <OperationTookTooLongGuard
                isLoading={isLoading}
                tooLongTimeInSeconds={getAppConfig().logInStallTimeout ?? 30}
            />
            <Paper
                elevation={4}
                sx={{
                    width: '100%',
                    maxWidth: 440,
                    p: 3,
                    borderRadius: 2,
                    boxSizing: 'border-box',
                }}
            >
                <Stack spacing={2}>
                    {ssoHeader}
                    {logo ? (
                        <Box sx={{ width: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                            {logo}
                        </Box>
                    ) : null}
                    {errorMessage && (
                        <Box sx={{ width: '100%' }}>
                            {errorMessage
                                .split('\n')
                                .filter((item) => item.length > 5)
                                .map((value, index) => (
                                    <Alert
                                        severity="error"
                                        testId={`login-error-${index}`}
                                        // eslint-disable-next-line react/no-array-index-key
                                        key={`login-error-${index}`}
                                        sx={{ my: 1 }}
                                    >
                                        <Typography component="span" variant="caption">
                                            {value}
                                        </Typography>
                                    </Alert>
                                ))}
                        </Box>
                    )}
                    {successMessage && (
                        <Box sx={{ width: '100%' }}>
                            {successMessage
                                .split('\n')
                                .filter((item) => item.length > 5)
                                .map((value, index) => (
                                    <Alert
                                        severity="success"
                                        testId={`login-success-${index}`}
                                        // eslint-disable-next-line react/no-array-index-key
                                        key={`login-success-${index}`}
                                        sx={{ my: 1 }}
                                    >
                                        <Typography component="span" variant="caption">
                                            {value}
                                        </Typography>
                                    </Alert>
                                ))}
                        </Box>
                    )}
                    <LoginPageContent
                        isBuyer={isBuyer}
                        formVariant={formVariant}
                        userLogin={userLogin}
                        isLoading={isLoading}
                        email={email}
                        password={password}
                        companyId={companyId}
                        emailError={emailError}
                        passwordError={passwordError}
                        companyIdError={companyIdError}
                        handleEmailChange={handleEmailChange}
                        handlePasswordChange={handlePasswordChange}
                        handleCompanyIdChange={handleCompanyIdChange}
                        handleSignIn={handleSignIn}
                        handleForgotPassword={handleForgotPassword}
                        handleReset={handleReset}
                        handleBackToSignIn={handleBackToSignIn}
                        handleSSOSignIn={handleSSOSignIn}
                        handleLogInAgain={handleLogInAgain}
                        useCardFormLayout
                    />
                    <LoginCardFooter />
                </Stack>
            </Paper>
        </Box>
    );
};

export default LoginContainerNew;
